import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import type { Idea } from '@/lib/content';
import { CATEGORIES } from '@/lib/content';

interface IdeaCardProps {
  idea: Idea;
  variant?: 'default' | 'compact' | 'featured';
}

export default function IdeaCard({ idea, variant = 'default' }: IdeaCardProps) {
  const category = CATEGORIES.find(c => c.id === idea.primary_category);

  if (variant === 'featured') {
    return (
      <Link href={`/sketches/${idea.slug}`} className="block group">
        <article className="sketch-card overflow-hidden">
          <div className="sketch-thumbnail" style={{ aspectRatio: '16/10' }}>
            <AppImage
              src={idea.illustration_url}
              alt={idea.illustration_alt}
              width={900}
              height={563}
              priority
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-103"
            />
          </div>
          <div className="p-6 sm:p-8">
            {category && (
              <span className="category-pill mb-3 inline-flex">{category.name}</span>
            )}
            <h3
              className="font-display font-bold text-foreground text-2xl sm:text-3xl tracking-tight leading-tight mb-3 group-hover:text-accent transition-colors duration-200"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {idea.title}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{idea.reader_hook}</p>
          </div>
        </article>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link href={`/sketches/${idea.slug}`} className="block group">
        <article className="related-idea-card">
          <div className="sketch-thumbnail" style={{ aspectRatio: '3/2' }}>
            <AppImage
              src={idea.illustration_url}
              alt={idea.illustration_alt}
              width={400}
              height={267}
              className="w-full h-full object-cover transition-transform duration-600 group-hover:scale-103"
            />
          </div>
          <div className="p-4">
            {category && (
              <span className="category-pill mb-2 inline-flex">{category.name}</span>
            )}
            <h4
              className="font-display font-semibold text-foreground text-base leading-snug group-hover:text-accent transition-colors duration-200"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {idea.title}
            </h4>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link href={`/sketches/${idea.slug}`} className="block group">
      <article className="sketch-card h-full flex flex-col">
        <div className="sketch-thumbnail">
          <AppImage
            src={idea.illustration_url}
            alt={idea.illustration_alt}
            width={600}
            height={450}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-5 flex flex-col flex-1 gap-2.5">
          {category && (
            <span className="category-pill inline-flex self-start">{category.name}</span>
          )}
          <h3
            className="font-display font-semibold text-foreground text-lg leading-snug group-hover:text-accent transition-colors duration-200"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {idea.title}
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed flex-1">{idea.reader_hook}</p>
          <div className="flex items-center gap-1.5 text-accent text-sm font-medium mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            Read more
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="translate-x-0 group-hover:translate-x-0.5 transition-transform duration-200">
              <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </article>
    </Link>
  );
}